// SightingsByUploadProvider - wrap a list surface in this and every descendant
// can render an upload's "seen at" provenance without prop-drilling or a second
// fetch. Mirrors UploadsExtractedProvider, and is fed from the SAME /uploads/list
// poll the Files tab already runs (principle 31: one list poll serves the whole
// surface, never a per-row request).
//
// Exists because the Gmail tree renders its attachments as nested child rows fed
// by the THREAD's attachment manifest (useEmailThreadManifest), not by /uploads/tree - so
// those rows have an upload_id but no sightings of their own to render. The list
// query has them for every upload in the project, including the nested ones - and
// the same holds for the wrong-site suspicion badge, so it is served from here too.
import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { PossibleOtherSite, Sighting, UploadRecord } from "./useUploadsList";

const SightingsByUploadContext = createContext<Record<string, Sighting[]>>({});
const PossibleOtherSiteContext = createContext<Record<string, PossibleOtherSite>>({});

export const SightingsByUploadProvider = ({
  uploads,
  children,
}: {
  uploads: UploadRecord[];
  children: ReactNode;
}): JSX.Element => {
  const value = useMemo<Record<string, Sighting[]>>(() => {
    const map: Record<string, Sighting[]> = {};
    for (const u of uploads) {
      if (u.sightings && u.sightings.length > 0) map[u.upload_id] = u.sightings;
    }
    return map;
  }, [uploads]);
  const possibleOtherSite = useMemo<Record<string, PossibleOtherSite>>(() => {
    const map: Record<string, PossibleOtherSite> = {};
    for (const u of uploads) {
      if (u.possible_other_site) map[u.upload_id] = u.possible_other_site;
    }
    return map;
  }, [uploads]);
  return (
    <SightingsByUploadContext.Provider value={value}>
      <PossibleOtherSiteContext.Provider value={possibleOtherSite}>
        {children}
      </PossibleOtherSiteContext.Provider>
    </SightingsByUploadContext.Provider>
  );
};

// The sightings for one upload, or [] when the surface isn't wrapped in the
// provider (so a component can render provenance where it's available and simply
// omit it elsewhere, rather than crashing).
export const useSightingsFor = (uploadId: string | null | undefined): Sighting[] => {
  const byUpload = useContext(SightingsByUploadContext);
  if (!uploadId) return [];
  return byUpload[uploadId] ?? [];
};

// The wrong-site suspicion for one upload, or null (none, or not wrapped).
export const usePossibleOtherSiteFor = (
  uploadId: string | null | undefined,
): PossibleOtherSite | null => {
  const byUpload = useContext(PossibleOtherSiteContext);
  if (!uploadId) return null;
  return byUpload[uploadId] ?? null;
};
